import React from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { OverallStatusKey, ServiceGroup, sortEnvNames } from '../lib/grouping';
import { getColors, getElevation, rounded } from '../theme/tokens';
import { reduceMotionSx, transition } from '../theme/motion';
import { getStatusHue } from '../theme/statusTokens';
import StatusDot from './StatusDot';
import TypeBadge from './TypeBadge';
import { timeAgo } from '../lib/format';
import { serviceHref } from '../lib/records';

/** Maps grouping rollup status keys to StatusDot's record-status vocabulary. */
export const statusDotStatus: Record<OverallStatusKey, string> = {
  deploying: 'inprogress',
  failures: 'failed',
  pending: 'pending',
  healthy: 'success',
  mixed: 'unknown',
};

export interface ServiceCardProps {
  app: string;
  group: ServiceGroup;
}

/**
 * One service as a compact comparison card: status, app · type, current
 * version per environment, last deploy. Links to the service page. Used by
 * the Services directory (service-select mode) and the App page compare mode.
 */
const ServiceCard: React.FC<ServiceCardProps> = ({ app, group }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const envs = sortEnvNames(Array.from(group.byEnv.keys()));
  return (
    <Paper
      component={RouterLink}
      to={serviceHref(app, group.serviceName, group.type)}
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: `${rounded.lg}px`,
        borderColor: c.hairline,
        textDecoration: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        minWidth: 0,
        transition: transition(['border-color', 'box-shadow', 'transform']),
        '&:hover': {
          borderColor: c.inkFaint,
          transform: 'translateY(-2px)',
          boxShadow: getElevation(theme.palette.mode).soft,
        },
        ...reduceMotionSx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <StatusDot status={statusDotStatus[group.status]} />
        <Typography
          sx={{ fontWeight: 600, fontSize: '0.95rem', color: c.ink }}
          noWrap
          title={group.serviceName}
        >
          {group.serviceName}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: c.inkMuted }} noWrap>
          {app}
        </Typography>
        <TypeBadge type={group.type} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
        {envs.map(env => {
          const rec = group.byEnv.get(env)!;
          return (
            <Tooltip
              key={env}
              title={`${rec.status || 'unknown'} · ${timeAgo(rec.timestamp)}`}
              arrow
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: getStatusHue(rec.status),
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: c.inkSecondary, fontWeight: 600, width: 64, flexShrink: 0 }}
                  noWrap
                >
                  {env}
                  {rec.ttl != null ? ' ⏳' : ''}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: c.inkMuted,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.72rem',
                  }}
                  noWrap
                  title={rec.version}
                >
                  {rec.version || '—'}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {group.lastTimestamp && (
        <Typography variant="caption" sx={{ color: c.inkFaint }}>
          Last deploy {timeAgo(group.lastTimestamp)}
        </Typography>
      )}
    </Paper>
  );
};

export default ServiceCard;
